import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import ImageLightbox from "@/components/base/ImageLightbox";
import NewsCard from "@/components/feature/NewsCard";
import EmptyState from "@/components/base/EmptyState";
import { newsList } from "@/mocks/news";
import { NEWS_CATEGORIES } from "@/mocks/types";
import { formatDate } from "@/utils/format";

export default function NewsDetail() {
  const { id } = useParams();
  const news = newsList.find((n) => n.id === id);
  const [preview, setPreview] = useState<string | null>(null);

  if (!news) {
    return (
      <SiteLayout
        title="资讯不存在 - 91动漫"
        description="该资讯可能已被删除或链接有误，可返回动漫资讯列表继续浏览。"
        keywords="动漫资讯,91动漫"
      >
        <section className="w-full px-4 md:px-8 py-16 md:py-24 bg-background-50">
          <EmptyState
            icon="ri-error-warning-line"
            title="资讯不存在或已被删除"
            desc="你访问的资讯可能已被移除。可以回到资讯列表，看看最新的动漫动态。"
            actionLabel="返回资讯列表"
            actionTo="/news"
          />
        </section>
      </SiteLayout>
    );
  }

  const related = newsList
    .filter((n) => n.id !== news.id)
    .sort((a, b) => {
      const aSame = news.relatedAnimeIds.some((x) => a.relatedAnimeIds.includes(x)) ? 0 : 1;
      const bSame = news.relatedAnimeIds.some((x) => b.relatedAnimeIds.includes(x)) ? 0 : 1;
      if (aSame !== bSame) return aSame - bSame;
      return a.publishedAt < b.publishedAt ? 1 : -1;
    })
    .slice(0, 4);

  const sameCategory = newsList.filter((n) => n.category === news.category && n.id !== news.id).slice(0, 3);
  const categoryMeta = NEWS_CATEGORIES.find((c) => c.key === news.category);

  return (
    <SiteLayout
      title={`${news.title} - 91动漫`}
      description={news.summary}
      keywords={`${news.category},${news.tags.join(",")},动漫资讯,91动漫`}
    >
      <article className="w-full bg-background-50">
        <div className="w-full px-4 md:px-8 pt-6 md:pt-8">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: "动漫资讯", to: "/news" },
              { label: news.category },
            ]}
          />
        </div>

        <div className="w-full px-4 md:px-8 py-6 md:py-10">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8 lg:gap-12">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <Link
                  to={`/news?category=${encodeURIComponent(news.category)}`}
                  className="px-2.5 py-1 rounded-md bg-primary-100 text-primary-700 text-[11px] md:text-xs font-label hover:bg-primary-200 transition-colors duration-200 cursor-pointer"
                >
                  {news.category}
                </Link>
                {news.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-900 text-[11px] md:text-xs font-label"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              <h1 className="mt-4 font-heading font-bold text-foreground-950 text-2xl md:text-[34px] leading-[1.3] tracking-tight">
                {news.title}
              </h1>

              <div className="mt-4 flex flex-wrap items-center gap-4 text-xs md:text-sm text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-user-3-line" />
                  </span>
                  {news.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-calendar-line" />
                  </span>
                  {formatDate(news.publishedAt)}
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-eye-line" />
                  </span>
                  {news.readCount} 阅读
                </span>
              </div>

              <figure className="mt-6">
                <button
                  type="button"
                  onClick={() => setPreview(news.cover)}
                  className="block w-full rounded-lg overflow-hidden bg-background-200 cursor-zoom-in group"
                  aria-label="放大查看封面图片"
                >
                  <img
                    src={news.cover}
                    alt={news.title}
                    title={`${news.title} 资讯封面`}
                    className="w-full aspect-[16/9] object-cover transition-transform duration-500 ease-smooth group-hover:scale-[1.02]"
                  />
                </button>
                <figcaption className="mt-2 text-[11px] md:text-xs text-foreground-500 flex items-center gap-1.5">
                  <span className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-image-line" />
                  </span>
                  图片可点击放大预览 · 图文内容为演示数据
                </figcaption>
              </figure>

              <div className="mt-8 space-y-5">
                <p className="text-sm md:text-base text-foreground-700 leading-[1.9] pl-4 border-l-[3px] border-primary-400 bg-primary-50/60 py-2 pr-4 rounded-r-md">
                  {news.summary}
                </p>
                {news.content.map((block, idx) => {
                  if (block.type === "subtitle") {
                    return (
                      <h2
                        key={idx}
                        className="pt-3 font-heading font-bold text-foreground-950 text-lg md:text-xl flex items-center gap-2.5"
                      >
                        <span className="w-1.5 h-4 rounded-full bg-primary-500" />
                        {block.text}
                      </h2>
                    );
                  }
                  if (block.type === "quote") {
                    return (
                      <blockquote
                        key={idx}
                        className="px-5 py-4 rounded-lg bg-background-100 text-sm md:text-base text-foreground-800 leading-relaxed"
                      >
                        {block.text}
                      </blockquote>
                    );
                  }
                  if (block.type === "image") {
                    return (
                      <figure key={idx} className="pt-2">
                        <button
                          type="button"
                          onClick={() => setPreview(block.src ?? null)}
                          className="block w-full rounded-lg overflow-hidden bg-background-200 cursor-zoom-in group"
                          aria-label="放大查看正文图片"
                        >
                          <img
                            src={block.src}
                            alt={block.caption ?? news.title}
                            title={`${news.title} 配图`}
                            className="w-full aspect-video object-cover transition-transform duration-500 ease-smooth group-hover:scale-[1.02]"
                          />
                        </button>
                        {block.caption && (
                          <figcaption className="mt-2 text-[11px] md:text-xs text-foreground-500 text-center">
                            {block.caption}
                          </figcaption>
                        )}
                      </figure>
                    );
                  }
                  return (
                    <p key={idx} className="text-sm md:text-base text-foreground-700 leading-[1.95]">
                      {block.text}
                    </p>
                  );
                })}
              </div>

              <div className="mt-10 pt-6 border-t border-background-200">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-foreground-500">所属分类：</span>
                    <Link
                      to="/news"
                      className="px-3 h-8 inline-flex items-center rounded-full bg-background-100 border border-background-200 text-xs text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors duration-200 cursor-pointer"
                    >
                      动漫资讯
                    </Link>
                    <Link
                      to={`/news?category=${encodeURIComponent(news.category)}`}
                      className="px-3 h-8 inline-flex items-center rounded-full bg-primary-100 text-primary-700 text-xs hover:bg-primary-200 transition-colors duration-200 cursor-pointer"
                    >
                      {news.category}
                    </Link>
                  </div>
                  <div className="flex gap-2">
                    <Link
                      to="/news"
                      className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md border border-background-300 text-xs md:text-sm text-foreground-800 hover:border-primary-400 hover:text-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-arrow-left-line" />
                      返回列表
                    </Link>
                    <Link
                      to="/"
                      className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-foreground-950 text-background-50 text-xs md:text-sm hover:bg-foreground-900 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-home-4-line" />
                      首页
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            <aside className="flex flex-col gap-5">
              <div className="rounded-lg border border-background-200 bg-background-100 p-4 md:p-5">
                <h2 className="font-heading font-semibold text-foreground-950 text-sm flex items-center gap-2">
                  <span className="w-5 h-5 flex items-center justify-center text-primary-600">
                    <i className="ri-links-line text-base" />
                  </span>
                  相关资讯
                </h2>
                <ul className="mt-3 space-y-3">
                  {related.map((item, idx) => (
                    <li key={item.id}>
                      <Link to={`/news/detail/${item.id}`} className="flex gap-3 group cursor-pointer">
                        <div className="w-16 h-12 shrink-0 rounded-md overflow-hidden bg-background-200">
                          <img
                            src={item.cover}
                            alt={item.title}
                            title={`${item.title} 相关资讯`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs md:text-sm text-foreground-800 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                            {item.title}
                          </p>
                          <p className="mt-1 text-[11px] text-foreground-400 font-label">
                            {String(idx + 1).padStart(2, "0")} · {formatDate(item.publishedAt)}
                          </p>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {categoryMeta && (
                <div className="rounded-lg bg-foreground-950 p-5">
                  <h2 className="font-heading font-semibold text-background-50 text-sm flex items-center gap-2">
                    <span className="w-5 h-5 flex items-center justify-center text-accent-400">
                      <i className={`${categoryMeta.icon} text-base`} />
                    </span>
                    {categoryMeta.label}
                  </h2>
                  <p className="mt-2 text-xs text-background-300 leading-relaxed">{categoryMeta.desc}</p>
                  {sameCategory.length > 0 && (
                    <ul className="mt-4 space-y-2.5">
                      {sameCategory.map((item) => (
                        <li key={item.id}>
                          <Link
                            to={`/news/detail/${item.id}`}
                            className="text-xs text-background-200 leading-relaxed line-clamp-2 hover:text-primary-400 transition-colors duration-200 cursor-pointer"
                          >
                            · {item.title}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                  <Link
                    to={`/news?category=${encodeURIComponent(news.category)}`}
                    className="mt-4 inline-flex items-center gap-1.5 text-xs text-primary-400 hover:text-primary-300 transition-colors duration-200 cursor-pointer"
                  >
                    进入该分类
                    <i className="ri-arrow-right-line" />
                  </Link>
                </div>
              )}
            </aside>
          </div>
        </div>

        <section className="w-full px-4 md:px-8 py-10 md:py-14 bg-background-100 border-t border-background-200">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-heading font-bold text-foreground-950 text-lg md:text-xl">你可能还想读</h2>
            <Link
              to="/news"
              className="inline-flex items-center gap-1 text-sm text-foreground-700 hover:text-primary-600 transition-colors duration-200 cursor-pointer"
            >
              全部资讯
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5">
            {related.slice(0, 3).map((item, idx) => (
              <NewsCard key={item.id} news={item} index={idx} layout="column" />
            ))}
          </div>
        </section>
      </article>

      <ImageLightbox src={preview} alt={news.title} onClose={() => setPreview(null)} />
    </SiteLayout>
  );
}